/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { AppData, AppStore } from 'src/core/stores/app/AppStore';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { truncateValue } from 'src/core/utils/number';
import { CustomTextExportedProjectSwagger } from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { AutoTaggingJobStore } from 'src/customText/core/stores/CustomTextAutoTaggingJobStore';
import { CustomTextClassificationStore } from 'src/customText/core/stores/CustomTextClassificationStore';
import { CustomTextDocumentMetadata, CustomTextDocumentStore } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextExtractionStore } from 'src/customText/core/stores/CustomTextExtractionStore';
import { CustomTextGptPredictionsStore } from 'src/customText/core/stores/CustomTextGptPredictionsStore';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { GptJobStore } from 'src/modules/gptPrediction/stores/GptJobStore';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';
import { TrainingServiceStatus } from 'src/modules/modelTraining/types/trainingTypes';

export type CustomTextProjectKind =
    | 'CustomSingleLabelClassification'
    | 'CustomMultiLabelClassification'
    | 'CustomEntityRecognition'
    | 'CustomHealthcare';

export enum SwitchingTypes {
    Stable = 0,
    Freezing = 1,
    Exporting = 2
}

export type CustomTextProjectStoreMetadata = AppData & {
    containerName: string;
    multiLingual: boolean;
    nextBatchExists: boolean;
    projectKind: CustomTextProjectKind;
    oosThreshold: number;
    documents: CustomTextDocumentMetadata[];
    isLabelingLocked: boolean;
    amlProjectPath: string;
    amlWorkspaceContainerName: string;
};

export type AMLConnectionInfo = {
    subscription: string;
    storageAccount?: string;
    containerName: string;
    labelingTaskType: string;
    projectLanguage: string;
    azureMLWorkspace?: string;
    projectName: string;
    outsourcing: boolean;
    vendor: string;
    storageResourceGroup: string;
    labelingInstructionText: string;
    labelingInstructionUrl: string;
    amlWorkspaceContainerName?: string;
    subscriptionId?: string;
    projectId?: string;
    datasetId?: string;
    labelNames?: string[];
    projectRoute?: string;
    workspaceRegion: string;
};

export type ProjectDomain = 'documents' | 'entities' | 'classes';
export type AmlProjectStates = 'Running' | 'Initializing' | 'Failed';

export class CustomTextProjectStore extends AppStore {
    public containerName: string;

    public projectKind: CustomTextProjectKind;

    @observable.shallow public extractionStore: CustomTextExtractionStore;

    @observable.shallow public classificationStore: CustomTextClassificationStore;

    @observable.shallow public jsonExportedData: CustomTextExportedProjectSwagger;

    @observable public evaluationJobStore: EvaluationJobStore;

    @observable public oosThreshold: number;

    @observable public amlConnectionInfo: AMLConnectionInfo;

    @observable public isLabelingLocked: boolean;

    @observable public amlProjectPath: string;

    @observable public amlProjectStatus: AmlProjectStates;

    @observable public amlWorkspaceContainerName: string;

    @observable public switchToAmlState: SwitchingTypes;

    public readonly gptPredictionsStore = new CustomTextGptPredictionsStore();

    public readonly gptJobsStoreHouse = createResourceStoreHouse<GptJobStore>();

    public readonly trainingJobStoreHouse = createResourceStoreHouse<TrainingJobStore>();

    public readonly trainedModels = createResourceStoreHouse<CustomTextTrainedModelStore>();

    public readonly autoTaggingJobStoreHouse = createResourceStoreHouse<AutoTaggingJobStore>();

    public resourceAssignmentsStoreHouse = createResourceStoreHouse<ResourceAssignmentStore>();

    public readonly deploymentsStoreHouse = createResourceStoreHouse<CustomSkillDeploymentStore>();

    @observable public autoTaggingQuota: { totalQuota: number; consumedQuota: number };

    @observable public savingState: { saved: boolean; latestChangeDomain?: ProjectDomain };

    @observable public multiLingual: boolean;

    @observable public nextBatchExists: boolean;

    @observable.shallow public documents: CustomTextDocumentMetadata[];

    @observable.shallow public currentDocumentStore: CustomTextDocumentStore;

    @observable public modelsEvaluationMap = new Map<string, ModelEvaluationStore>();

    @observable public modelsGuidanceMap = new Map<string, ModelGuidanceStore>();

    constructor(metadata: Partial<CustomTextProjectStoreMetadata>, store: CustomTextExtractionStore | CustomTextClassificationStore) {
        super(metadata.name, metadata.projectKind, metadata);

        this.savingState = { saved: true };
        this.projectKind = metadata.projectKind;
        this.name = metadata.name;
        this.multiLingual = metadata.multiLingual;
        this.isLabelingLocked = metadata.isLabelingLocked;
        this.amlWorkspaceContainerName = metadata.amlWorkspaceContainerName;
        this.amlProjectPath = metadata.amlProjectPath;
        this.documents = metadata.documents || [];
        this.containerName = metadata.containerName;
        this.nextBatchExists = metadata.nextBatchExists;
        this.oosThreshold = metadata.oosThreshold ?? 0;
        this.switchToAmlState = SwitchingTypes.Stable;
        if (metadata.projectKind === 'CustomEntityRecognition' || metadata.projectKind === 'CustomHealthcare') {
            this.extractionStore = <CustomTextExtractionStore>store;
        } else {
            this.classificationStore = <CustomTextClassificationStore>store;
        }
    }

    @action
    public updateProject(
        metadata: Partial<CustomTextProjectStoreMetadata>,
        documents: CustomTextDocumentMetadata[],
        store: CustomTextExtractionStore | CustomTextClassificationStore
    ) {
        this.description = metadata.description;
        this.multiLingual = metadata.multiLingual;
        if (this.projectKind !== 'CustomEntityRecognition') {
            this.oosThreshold = metadata.oosThreshold ?? 0;
        }
        this.setProjectDocuments(documents);
        this.setTagsStore(store, metadata.projectKind);
    }

    @computed
    public get labelsCount(): Map<string, number> {
        if (this.projectKind === 'CustomEntityRecognition' || this.projectKind === 'CustomHealthcare') {
            return this.documents.reduce((map, document) => {
                if (document.name === this.currentDocumentStore?.name) {
                    this.currentDocumentStore.entityLabels
                        .filter(label => label.labelType === 'normal')
                        .forEach(label => {
                            map.set(label.entityId, (map.get(label.entityId) ?? 0) + 1);
                        });
                } else {
                    document.entityLabels
                        .filter(label => label.labelType === 'normal')
                        .forEach(label => {
                            map.set(label.entityId, (map.get(label.entityId) ?? 0) + 1);
                        });
                }

                return map;
            }, new Map<string, number>());
        }

        return this.documents.reduce((map, document) => {
            if (document.name === this.currentDocumentStore?.name) {
                this.currentDocumentStore.entityLabels.forEach(label => {
                    map.set(label.entityId, (map.get(label.entityId) ?? 0) + 1);
                });
            } else {
                document.classLabels.forEach(cls => {
                    map.set(cls.id, (map.get(cls.id) ?? 0) + 1);
                });
            }

            return map;
        }, new Map<string, number>());
    }

    @computed
    public get isDirty(): boolean {
        return !this.savingState.saved;
    }

    @computed
    public get labeledDocumentsCount(): number {
        return this.documents.filter(d => d.classLabels.length || d.entityLabels.length).length;
    }

    @computed
    public get isControlDatasetUserDefined(): boolean {
        return this.documents.some(d => d.documentDataset === 'Test');
    }

    /**
     * Gets total number of tags in the project
     */
    @computed
    public get tagsCount(): number {
        return [...this.labelsCount.values()].reduce((val, acc) => acc + val, 0);
    }

    /**
     * Gets the percentage of documents in the training set defined by the user
     */
    @computed
    public get trainingSetPercentage(): number {
        return truncateValue((this.documents.filter(d => d.documentDataset === 'Train').length * 100) / this.labeledDocumentsCount, 2);
    }

    /**
     * Gets the percentage of documents in the testing set defined by the user
     */
    @computed
    public get testingSetPercentage(): number {
        return truncateValue((this.documents.filter(d => d.documentDataset === 'Test').length * 100) / this.labeledDocumentsCount, 2);
    }

    /**
     * Gets the model that is currently being trained.
     */
    @computed
    public get currentTrainingJob(): TrainingJobStore {
        return this.trainingJobStoreHouse.items.find(t => t.status === 'inProgress');
    }

    /**
     * Gets the auto-tagging job that is currently being running.
     */
    @computed
    public get currentRunningAutoTaggingJob(): AutoTaggingJobStore {
        return this.autoTaggingJobStoreHouse.items.find(j => j.status === 'inProgress');
    }

    /**
     * Gets the successfully trained model.
     */
    @computed
    public get successfullyTrainedModel(): CustomTextTrainedModelStore[] {
        return this.trainedModels.items;
    }

    /**
     * Gets whether a model is being trained or not.
     */
    @computed
    public get trainingServiceStatus(): TrainingServiceStatus {
        if (this.currentTrainingJob) {
            return 'inProgress';
        }

        return this.trainingJobStoreHouse.items.find(t => t.status === 'notStarted' || t.status === 'cancelling') ? 'queued' : 'ready';
    }

    @action
    public setTagsStore(store: CustomTextExtractionStore | CustomTextClassificationStore, projectKind: CustomTextProjectKind) {
        if (projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare') {
            this.extractionStore = <CustomTextExtractionStore>store;
        } else {
            this.classificationStore = <CustomTextClassificationStore>store;
        }
    }

    @action
    public setAutoTaggingQuota(quota: CustomTextProjectStore['autoTaggingQuota']) {
        this.autoTaggingQuota = quota;
    }

    @action
    public setIsDirty(isDirty: boolean, latestChangeDomain?: ProjectDomain) {
        this.savingState = { saved: !isDirty, latestChangeDomain };
    }

    @action
    public setProjectDocuments(documentsMetadata: CustomTextDocumentMetadata[]): void {
        this.documents = documentsMetadata;
    }

    @action
    public setCurrentDocumentStore(documentStore: CustomTextDocumentStore) {
        this.currentDocumentStore = documentStore;
    }

    @action
    public addModelEvaluationMapItem(k: string, val: ModelEvaluationStore): void {
        this.modelsEvaluationMap.set(k, val);
    }

    @action
    public addModelGuidanceMapItem(k: string, val: ModelGuidanceStore): void {
        this.modelsGuidanceMap.set(k, val);
    }

    @action
    public setOosThreshold(oosThreshold: number) {
        this.oosThreshold = oosThreshold ?? 0;
    }

    @action
    public setMultiLingual(multiLingual: boolean): void {
        this.multiLingual = multiLingual;
    }

    @action
    public setAmlConnectionInfo(amlInfo: Partial<AMLConnectionInfo>): void {
        this.amlConnectionInfo = { ...this.amlConnectionInfo, ...amlInfo };
    }

    @action
    public setIslabelingLocked(isLabelingLocked: boolean): void {
        this.isLabelingLocked = isLabelingLocked;
    }

    @action
    public setAmlProjectPath(amlProjectPath = ''): void {
        this.amlProjectPath = amlProjectPath;
    }

    @action
    public setAmlProjectStatus(status: AmlProjectStates): void {
        this.amlProjectStatus = status;
    }

    @action
    public setAmlWorkspaceContainerName(val: string): void {
        this.amlWorkspaceContainerName = val;
    }

    @action
    public setSwitchToAmlState(val: SwitchingTypes): void {
        this.switchToAmlState = val;
    }

    @action
    public setEvaluationJobStore(val: EvaluationJobStore): void {
        this.evaluationJobStore = val;
    }

    /**
     * Updates the container marker to indicate the next blob to be fetched from the container
     */
    @action
    public setNextBatchExists(value: boolean): void {
        this.nextBatchExists = value;
    }

    @action
    public setJsonExportedData(value: CustomTextExportedProjectSwagger): void {
        this.jsonExportedData = value;
    }
}
